import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { clearSessionExpiredNotification, setSessionExpiredHandler } from '../utils/sessionExpiry';

/**
 * Shows a re-login prompt when the session expires, instead of the silent logout
 * that used to fire from the auth interceptors. The user can re-authenticate
 * (returning to where they were) or sign out — but is never dropped without notice
 * mid-edit. Non-dismissable: the session is gone, so a choice is required.
 */
const SessionExpiredDialog: React.FC = () => {
  const { login, logout } = useAuth();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Guard against re-opening if multiple 401s fire while the dialog is already up.
    setSessionExpiredHandler(() => setOpen(true));
    return () => setSessionExpiredHandler(null);
  }, []);

  const handleReauthenticate = () => {
    clearSessionExpiredNotification();
    setOpen(false);
    login();
  };

  const handleSignOut = () => {
    clearSessionExpiredNotification();
    setOpen(false);
    logout();
  };

  return (
    <Dialog open={open} disableEscapeKeyDown aria-labelledby="session-expired-title">
      <DialogTitle id="session-expired-title">Session expired</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Your session has expired for security reasons. Sign in again to continue where you
          left off, or sign out.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleSignOut} color="inherit">
          Sign out
        </Button>
        <Button onClick={handleReauthenticate} variant="contained" autoFocus>
          Sign in again
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SessionExpiredDialog;
